import {
  useForm,
  FormProvider,
  type DefaultValues,
  type FieldValues,
  type UseFormSetError,
} from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ReactNode, FormHTMLAttributes } from "react";
import type { ZodType, z } from "zod";

/**
 * Helpers passed as the second argument to `onSubmit`. Lets a submit handler
 * surface server-side validation on the matching fields — e.g. map a DRF
 * field-error response to RHF via `setError`. Optional: handlers that only need
 * the values can ignore it (a one-arg handler is still assignable).
 */
export type FormHelpers<T> = {
  setError: UseFormSetError<T & FieldValues>;
};

/**
 * Form component — instantiating one REQUIRES a Zod schema. Per Hector's
 * kickoff spec: "Form component should have validation embedded, so when a
 * form is instanced the validation params would be required."
 *
 * TypeScript enforces this — `<Form>` without a `schema` prop is a type
 * error, so callers can't accidentally ship a validation-free form.
 *
 * Wraps react-hook-form's `useForm` + `FormProvider`. The schema's inferred
 * type flows through to `defaultValues` and `onSubmit(values)`. Use
 * `<FormField name="…">` inside to bind individual fields.
 *
 * Validation mode: `onBlur` by default (matches the four-state UX rule —
 * users see errors after leaving the field, not on every keystroke).
 * Re-validates on change once a field has been touched.
 */
type FormProps<S extends ZodType> = Omit<
  FormHTMLAttributes<HTMLFormElement>,
  "onSubmit" | "defaultValue" | "children"
> & {
  schema: S;
  defaultValues?: DefaultValues<z.infer<S>>;
  onSubmit: (values: z.infer<S>, helpers: FormHelpers<z.infer<S>>) => void | Promise<void>;
  children: ReactNode;
};

export function Form<S extends ZodType>({
  schema,
  defaultValues,
  onSubmit,
  children,
  ...formProps
}: FormProps<S>) {
  const methods = useForm<z.infer<S>>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  return (
    <FormProvider {...methods}>
      <form
        noValidate
        onSubmit={methods.handleSubmit((values) =>
          onSubmit(values, {
            setError: methods.setError as UseFormSetError<z.infer<S> & FieldValues>,
          }),
        )}
        {...formProps}
      >
        {children}
      </form>
    </FormProvider>
  );
}
